import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import Edit from 'shared/ui/icons/General/Edit';
import {MonitorMenu, MonitorTagsModal} from 'entities/monitor';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import YesNo from 'shared/ui/YesNo/YesNo';
import DateWithColorWarnings from 'shared/ui/Date/DateWithColorWarnings/DateWithColorWarnings';
import {ExceededModal, IUserOutput} from 'entities/user';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import RowTags from 'shared/ui/Table/RowTags/RowTags';
import IDomainMonitorOutput from 'features/domain/model/DomainMonitor/IDomainMonitor';
import useDomainMonitorRow from 'features/domain/model/useDomainMonitorRow';
import {isTranslate} from 'shared/lib/lang';


import useUpdateDomainMonitorTags from 'features/domain/model/useUpdateDomainMonitorTags';

import {PALETTE} from 'shared/config/model/palette';

import DomainCheckValue from '../DomainCheckValue/DomainCheckValue';

import DomainMonitorModal from '../DomainMonitorModal';
import DomainMonitorPauseModal from '../DomainMonitorPauseModal/DomainMonitorPauseModal';
import DomainMonitorResumeModal from '../DomainMonitorResumeModal/DomainMonitorResumeModal';
import DomainMonitorDeleteModal from '../DomainMonitorDeleteModal/DomainMonitorDeleteModal';

import styles from './DomainMonitorRow.module.scss';

interface IDomainMonitorRowProps {
    item: IDomainMonitorOutput,
    user: IUserOutput | null,
    hasSelectedItems: boolean,
}

const DomainMonitorRow: React.FC<IDomainMonitorRowProps> = ({item, user, hasSelectedItems}) => {
    const {
        activeModal,
        closeModal: handleCloseModal,
        canCreate,
        isSelected,
        dateFormat,
        onCheckboxChange: handleCheckboxChange,
        onTagClick,
        onDetailsClick,
        onEditClick,
        onPauseClick,
        onResumeClick,
        onDeleteClick,
        onTagsClick,
        onToggleClick,
    } = useDomainMonitorRow(item);

    const tzString = user?.timezone;
    const tags = item.tags ?? [];

    return (
        <>
            <tr
                className={cn(styles.row, !item.active && styles.isPaused)}
                onClick={onDetailsClick}
            >
                <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        onChange={handleCheckboxChange}
                        checked={isSelected}
                        className={styles.checkbox}
                    />
                </td>
                <td className={styles.blockName}>
                    <div className={styles.containerName}>
                        <span className={styles.name} title={item.domain_human_readable}>
                            {item.domain_human_readable}
                        </span>
                        <span className={styles.editIcon} onClick={onEditClick}>
                            <Edit/>
                        </span>
                    </div>
                    {tags.length > 0 && (
                        <RowTags tags={tags} onTagClick={onTagClick} disabled={hasSelectedItems}/>
                    )}
                </td>
                <td className={styles.dateCell}>
                    <DomainCheckValue
                        tariffCondition="monitor_ssl_enabled"
                        isWatched={item.ssl_watch}
                        value={item.ssl_active_to}
                    >
                        <DateWithColorWarnings
                            dateString={item.ssl_active_to}
                            options={{format: dateFormat, tzString}}
                        />
                    </DomainCheckValue>
                </td>
                <td className={styles.dateCell}>
                    <DomainCheckValue
                        tariffCondition="monitor_domain_watch_enabled"
                        isWatched={item.domain_watch}
                        value={item.domain_active_to}
                    >
                        <DateWithColorWarnings
                            dateString={item.domain_active_to}
                            options={{format: dateFormat, tzString}}
                        />
                    </DomainCheckValue>
                </td>
                <td>
                    <DomainCheckValue
                        tariffCondition="monitor_ssl_redirect_watch_enabled"
                        isWatched={item.ssl_redirect_watch}
                        value={item.ssl_redirect}
                    >
                        <YesNo condition={!!item.ssl_redirect} color={PALETTE.success}/>
                    </DomainCheckValue>
                </td>
                {!isTranslate() && (
                    <td>
                        <DomainCheckValue
                            tariffCondition="monitor_rkn_watch_enabled"
                            isWatched={item.rkn_watch}
                            value={item.rkn_block}
                        >
                            <YesNo condition={!!item.rkn_block} colorFalse={PALETTE.success} color={PALETTE.error}/>
                        </DomainCheckValue>
                    </td>
                )}
                <td className={styles.toggleCell}>
                    <StatusToggle
                        condition={item.active}
                        onClick={onToggleClick}
                        notActiveText={t`На паузе`}
                        preventToggle={!item.active && !canCreate}
                    />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                        <MonitorMenu
                            isActive={item.active}
                            onEditClick={onEditClick}
                            onDetailsClick={onDetailsClick}
                            onTagsClick={onTagsClick}
                            onPauseClick={onPauseClick}
                            onResumeClick={onResumeClick}
                            onDeleteClick={onDeleteClick}
                        />
                    </div>
                </td>
            </tr>

            <DomainMonitorModal
                item={item}
                open={activeModal === 'edit'}
                handleClose={handleCloseModal}
            />

            <DomainMonitorPauseModal
                item={item}
                open={activeModal === 'pause'}
                handleClose={handleCloseModal}
            />

            <DomainMonitorResumeModal
                item={item}
                open={activeModal === 'resume'}
                handleClose={handleCloseModal}
            />

            <DomainMonitorDeleteModal
                item={item}
                open={activeModal === 'delete'}
                handleClose={handleCloseModal}
            />

            <MonitorTagsModal
                uuid={item.uuid}
                tags={item.tags}
                useUpdateTags={useUpdateDomainMonitorTags}
                open={activeModal === 'tags'}
                handleClose={handleCloseModal}
            />

            <ExceededModal
                isOpen={activeModal === 'limit'}
                handleClose={handleCloseModal}
                type="domain_monitors"
            />
        </>
    );
};

export default DomainMonitorRow;
